import { useEffect, useRef, useState } from "react";
import logoMark from "@/assets/logo-mark.webp";

/**
 * Particle hero: samples the bright "TF" strokes from the logo and renders
 * them as a glowing particle field, with a soft octagon silhouette behind.
 */
type Tier = { step: number; markSize: number; ringSize: number; perSide: number };

function getTier(w: number): Tier {
  // Mobile / tablet / desktop tuned for ~30–50% fewer particles than before.
  if (w < 640) return { step: 5, markSize: 1.6, ringSize: 1.4, perSide: 14 };
  if (w < 1024) return { step: 4, markSize: 2.0, ringSize: 1.7, perSide: 18 };
  return { step: 3, markSize: 2.4, ringSize: 2.0, perSide: 22 };
}

export function ParticleHero({
  glowStrength = 1.4,
  brightnessThreshold = 150,
  verticalOffset = 0,
  horizontalOffset = 0,
}: {
  glowStrength?: number;
  brightnessThreshold?: number;
  /** Fraction of canvas height to lift the sphere upward (e.g. 0.18 = 18% up). */
  verticalOffset?: number;
  /** Fraction of canvas width to shift the sphere horizontally (positive = right). */
  horizontalOffset?: number;
}) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const mouseTarget = useRef({ x: 0, y: 0, active: false });
  const mouse = useRef({ x: 0, y: 0 });
  // The canvas is decorative and sits behind the hero copy. Starting it during
  // hydration steals the main thread from the LCP paint, so wait for the first
  // idle slot (hard cap 1.5s) before sampling the logo and running the rAF loop.
  const [active, setActive] = useState(false);

  useEffect(() => {
    const ric = window.requestIdleCallback;
    if (ric) {
      const id = ric(() => setActive(true), { timeout: 1500 });
      return () => window.cancelIdleCallback?.(id);
    }
    const id = window.setTimeout(() => setActive(true), 800);
    return () => window.clearTimeout(id);
  }, []);

  useEffect(() => {
    if (!active) return;
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const dpr = Math.min(window.devicePixelRatio || 1, window.innerWidth < 640 ? 1.5 : 2);
    let w = 0;
    let h = 0;
    let tier: Tier = getTier(window.innerWidth);

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const next = getTier(window.innerWidth);
      if (next.step !== tier.step || next.perSide !== tier.perSide) {
        tier = next;
        buildPoints();
      } else {
        tier = next;
      }
    };

    let markPts: { x: number; y: number; phase: number }[] = [];
    let ringPts: { x: number; y: number; phase: number }[] = [];
    let logoData: Uint8ClampedArray | null = null;
    const SAMPLE = 220;

    const buildMark = () => {
      if (!logoData) return;
      const mark: typeof markPts = [];
      for (let y = 0; y < SAMPLE; y += tier.step) {
        for (let x = 0; x < SAMPLE; x += tier.step) {
          const i = (y * SAMPLE + x) * 4;
          const a = logoData[i + 3];
          if (a < 50) continue;
          const lum = (logoData[i] + logoData[i + 1] + logoData[i + 2]) / 3;
          if (lum > brightnessThreshold) {
            mark.push({
              x: x / SAMPLE - 0.5,
              y: y / SAMPLE - 0.5,
              phase: Math.random() * Math.PI * 2,
            });
          }
        }
      }
      markPts = mark;
    };

    const buildRing = () => {
      const ring: typeof ringPts = [];
      const sides = 10;
      const radius = 0.46;
      for (let s = 0; s < sides; s++) {
        const a1 = (s / sides) * Math.PI * 2 - Math.PI / 2;
        const a2 = ((s + 1) / sides) * Math.PI * 2 - Math.PI / 2;
        const x1 = Math.cos(a1) * radius;
        const y1 = Math.sin(a1) * radius;
        const x2 = Math.cos(a2) * radius;
        const y2 = Math.sin(a2) * radius;
        for (let k = 0; k < tier.perSide; k++) {
          const t = k / tier.perSide;
          ring.push({
            x: x1 + (x2 - x1) * t,
            y: y1 + (y2 - y1) * t,
            phase: Math.random() * Math.PI * 2,
          });
        }
      }
      ringPts = ring;
    };

    const buildPoints = () => {
      buildMark();
      buildRing();
    };

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = logoMark;

    img.onload = () => {
      const off = document.createElement("canvas");
      off.width = SAMPLE;
      off.height = SAMPLE;
      const octx = off.getContext("2d");
      if (!octx) return;
      octx.drawImage(img, 0, 0, SAMPLE, SAMPLE);
      logoData = octx.getImageData(0, 0, SAMPLE, SAMPLE).data;
      buildPoints();
    };

    let raf = 0;
    let t = 0;
    let last = performance.now();
    let visible = true;
    let inView = true;

    const onMove = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouseTarget.current.x = (e.clientX - rect.left) / rect.width - 0.5;
      mouseTarget.current.y = (e.clientY - rect.top) / rect.height - 0.5;
      mouseTarget.current.active = true;
    };
    const onLeave = () => (mouseTarget.current.active = false);
    window.addEventListener("pointermove", onMove, { passive: true });
    canvas.addEventListener("pointerleave", onLeave);

    const draw = (now: number) => {
      if (!visible || !inView) {
        // Don't burn CPU when tab is hidden or hero is scrolled offscreen.
        last = now;
        raf = requestAnimationFrame(draw);
        return;
      }
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      t += dt;

      ctx.clearRect(0, 0, w, h);

      const cx = w / 2;
      const cy = h / 2 - h * verticalOffset;
      const cxAdj = cx + w * horizontalOffset;
      const scale = Math.min(w, h) * 0.45;

      const glow = ctx.createRadialGradient(cxAdj, cy, 0, cxAdj, cy, scale * 0.75);
      glow.addColorStop(0, `rgba(198, 248, 78, ${0.28 * glowStrength})`);
      glow.addColorStop(0.5, `rgba(198, 248, 78, ${0.08 * glowStrength})`);
      glow.addColorStop(1, "rgba(198, 248, 78, 0)");
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(cxAdj, cy, scale * 0.75, 0, Math.PI * 2);
      ctx.fill();

      // Smoothed parallax: ease mouse toward its target each frame.
      const ease = Math.min(1, dt * 6);
      mouse.current.x += (mouseTarget.current.x - mouse.current.x) * ease;
      mouse.current.y += (mouseTarget.current.y - mouse.current.y) * ease;

      const idleX = Math.sin(t * 0.4) * 0.04;
      const idleY = Math.cos(t * 0.3) * 0.035;
      const tiltX = reduceMotion ? 0 : (mouseTarget.current.active ? mouse.current.x * 0.22 : idleX);
      const tiltY = reduceMotion ? 0 : (mouseTarget.current.active ? mouse.current.y * 0.18 : idleY);
      const offX = reduceMotion ? 0 : mouse.current.x * scale * 0.06;
      const offY = reduceMotion ? 0 : mouse.current.y * scale * 0.06;
      const float = reduceMotion ? 0 : Math.sin(t * 0.7) * 4;

      for (let i = 0; i < ringPts.length; i++) {
        const p = ringPts[i];
        const px = p.x + tiltX * p.y;
        const py = p.y - tiltY * p.x;
        const sx = cxAdj + px * scale + offX;
        const sy = cy + py * scale + float + offY;
        const a = 0.55 + Math.sin(t * 1.6 + p.phase) * 0.3;
        ctx.fillStyle = `rgba(198, 248, 78, ${a})`;
        ctx.beginPath();
        ctx.arc(sx, sy, tier.ringSize, 0, Math.PI * 2);
        ctx.fill();
      }

      for (let i = 0; i < markPts.length; i++) {
        const p = markPts[i];
        const j = Math.sin(t * 1.4 + p.phase) * 0.003;
        const px = p.x + j + tiltX * p.y;
        const py = p.y + j - tiltY * p.x;
        const sx = cxAdj + px * scale + offX;
        const sy = cy + py * scale + float + offY;
        const pulse = 0.75 + Math.sin(t * 2 + p.phase) * 0.25;
        ctx.fillStyle = `rgba(244, 245, 240, ${pulse})`;
        ctx.beginPath();
        ctx.arc(sx, sy, tier.markSize, 0, Math.PI * 2);
        ctx.fill();
      }

      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    const onVis = () => { visible = !document.hidden; last = performance.now(); };
    document.addEventListener("visibilitychange", onVis);
    const io = new IntersectionObserver(([entry]) => {
      inView = entry.isIntersecting;
      last = performance.now();
    }, { threshold: 0.01 });
    io.observe(canvas);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("pointerleave", onLeave);
    };
  }, [active, glowStrength, brightnessThreshold, verticalOffset, horizontalOffset]);

  return (
    <canvas
      ref={ref}
      className="absolute inset-0 h-full w-full"
      aria-hidden="true"
    />
  );
}